import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, ChevronRight, FileText } from "lucide-react";
import { toast } from "sonner";
import { usePriorities } from "@/context/PrioritiesContext";
import BulkTaxonomyDialog from "@/components/settings/BulkTaxonomyDialog";
import { useAuth } from "@/context/AuthContext";

export default function TaxonomyManager() {
  const { hasPerm } = useAuth();
  const canCreateMain = hasPerm("catalogue.create_categories");
  const canEditMain = hasPerm("catalogue.edit_categories");
  const canDeleteMain = hasPerm("catalogue.delete_categories");
  const canCreateSub = hasPerm("catalogue.create_subcategories");
  const canEditSub = hasPerm("catalogue.edit_subcategories");
  const canDeleteSub = hasPerm("catalogue.delete_subcategories");
  const canManageIssue = hasPerm("catalogue.manage_issue_types");
  const { priorities, priorityOptions } = usePriorities();
  const slaResMins = (key) => priorities.find((p) => p.key === key)?.resolution_minutes;
  const slaFrMins = (key) => priorities.find((p) => p.key === key)?.first_response_minutes;
  const fmtSla = (m) => (!m ? "—" : m < 60 ? `${m}m` : (m % 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m / 60}h`));
  const [mains, setMains] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [subs, setSubs] = useState([]);
  const [issues, setIssues] = useState([]);
  const [departments, setDepts] = useState([]);
  const [caseTypes, setCaseTypes] = useState([]);

  const [selMain, setSelMain] = useState("");
  const [selSub, setSelSub] = useState("");

  const loadMains = () => api.get("/main-categories").then((r) => setMains(r.data));
  const loadSubs = (mid) =>
    mid
      ? api.get("/sub-categories", { params: { main_category_id: mid } }).then((r) => setSubs(r.data))
      : setSubs([]);
  const loadIssues = (sid) =>
    sid
      ? api.get("/issue-types", { params: { sub_category_id: sid } }).then((r) => setIssues(r.data))
      : setIssues([]);

  useEffect(() => {
    loadMains();
    api.get("/departments").then((r) => setDepts(r.data));
    api.get("/case-types").then((r) => setCaseTypes(r.data));
  }, []);
  useEffect(() => { loadSubs(selMain); setSelSub(""); setIssues([]); }, [selMain]);
  useEffect(() => { loadIssues(selSub); }, [selSub]);

  // Dialogs
  const [mainOpen, setMainOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [editMain, setEditMain] = useState(null);
  const [editSub, setEditSub] = useState(null);
  const [editIssue, setEditIssue] = useState(null);

  // Forms
  const [mainForm, setMainForm] = useState({ name: "", active: true });
  const [subForm, setSubForm] = useState({ name: "", main_category_id: "", active: true });
  const [issueForm, setIssueForm] = useState({
    name: "", sub_category_id: "", department_id: "", case_type_id: "",
    priority: "medium", sla_minutes: 480, branch_required: false, active: true,
  });

  const startMain = (m) => {
    setEditMain(m);
    setMainForm(m ? { name: m.name, active: m.active } : { name: "", active: true });
    setMainOpen(true);
  };
  const submitMain = async () => {
    if (!mainForm.name.trim()) return toast.error("Name required");
    try {
      if (editMain) await api.patch(`/main-categories/${editMain.id}`, mainForm);
      else await api.post("/main-categories", mainForm);
      toast.success("Saved"); setMainOpen(false); loadMains();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const deleteMain = async (m) => {
    if (!window.confirm(`Delete "${m.name}" and all its sub-categories will be orphaned?`)) return;
    await api.delete(`/main-categories/${m.id}`); loadMains();
    if (selMain === m.id) setSelMain("");
  };

  const startSub = (s) => {
    setEditSub(s);
    setSubForm(s
      ? { name: s.name, main_category_id: s.main_category_id, active: s.active }
      : { name: "", main_category_id: selMain, active: true });
    setSubOpen(true);
  };
  const submitSub = async () => {
    if (!subForm.name.trim()) return toast.error("Name required");
    if (!subForm.main_category_id) return toast.error("Pick a Main Category first");
    try {
      if (editSub) await api.patch(`/sub-categories/${editSub.id}`, subForm);
      else await api.post("/sub-categories", subForm);
      toast.success("Saved"); setSubOpen(false); loadSubs(selMain);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const deleteSub = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    await api.delete(`/sub-categories/${s.id}`); loadSubs(selMain);
    if (selSub === s.id) setSelSub("");
  };

  const startIssue = (it) => {
    setEditIssue(it);
    setIssueForm(it
      ? {
          name: it.name, sub_category_id: it.sub_category_id,
          department_id: it.department_id || "", case_type_id: it.case_type_id || "",
          priority: it.priority, sla_minutes: it.sla_minutes,
          branch_required: it.branch_required, active: it.active,
        }
      : {
          name: "", sub_category_id: selSub,
          department_id: "", case_type_id: "",
          priority: "medium", sla_minutes: 480, branch_required: false, active: true,
        });
    setIssueOpen(true);
  };
  const submitIssue = async () => {
    if (!issueForm.name.trim()) return toast.error("Name required");
    if (!issueForm.sub_category_id) return toast.error("Pick a Sub-Category first");
    try {
      if (editIssue) await api.patch(`/issue-types/${editIssue.id}`, issueForm);
      else await api.post("/issue-types", issueForm);
      toast.success("Saved"); setIssueOpen(false); loadIssues(selSub);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const deleteIssue = async (it) => {
    if (!window.confirm(`Delete "${it.name}"?`)) return;
    await api.delete(`/issue-types/${it.id}`); loadIssues(selSub);
  };

  const deptName = (id) => departments.find((d) => d.id === id)?.name || "—";
  const caseName = (id) => caseTypes.find((c) => c.id === id)?.name || "—";

  return (
    <div className="space-y-5" data-testid="taxonomy-manager">
      <Card className="border-slate-200 bg-emerald-50/50 border-emerald-200">
        <CardContent className="p-4 text-xs text-emerald-900 flex items-center justify-between flex-wrap gap-2">
          <div><strong>Three-level dependency:</strong> Main Category → Sub-Category → Issue Type. The Issue Type carries the auto-fill rules (Department · Priority · SLA · Case Type · Branch-required).</div>
          {canManageIssue && (
            <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)} className="border-emerald-300 text-emerald-800 hover:bg-emerald-100" data-testid="bulk-import-taxonomy-btn">
              <FileText className="w-3.5 h-3.5 mr-1.5" /> Bulk import
            </Button>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* MAIN CATEGORIES */}
        <Card className="border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="font-heading text-sm">Main Categories</CardTitle>
            {canCreateMain && (
              <Button size="sm" variant="outline" onClick={() => startMain(null)} data-testid="add-main-cat">
                <Plus className="w-3.5 h-3.5" />
              </Button>
            )}
          </CardHeader>
          <CardContent className="p-0 max-h-[400px] overflow-y-auto">
            {mains.map((m) => (
              <div
                key={m.id}
                className={`flex items-center justify-between px-4 py-2.5 border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${selMain === m.id ? "bg-indigo-50" : ""}`}
                onClick={() => setSelMain(m.id)}
                data-testid={`main-cat-${m.id}`}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <ChevronRight className={`w-3 h-3 ${selMain === m.id ? "text-indigo-600" : "text-slate-300"}`} />
                  <span className="text-sm font-medium text-slate-800 truncate">{m.name}</span>
                </div>
                <div className="flex items-center gap-0.5">
                  {canEditMain && <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); startMain(m); }} className="h-7 w-7 p-0"><Pencil className="w-3 h-3" /></Button>}
                  {canDeleteMain && <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteMain(m); }} className="h-7 w-7 p-0 text-rose-600"><Trash2 className="w-3 h-3" /></Button>}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* SUB CATEGORIES */}
        <Card className="border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="font-heading text-sm">Sub-Categories</CardTitle>
            {canCreateSub && (
              <Button size="sm" variant="outline" onClick={() => startSub(null)} disabled={!selMain} data-testid="add-sub-cat">
                <Plus className="w-3.5 h-3.5" />
              </Button>
            )}
          </CardHeader>
          <CardContent className="p-0 max-h-[400px] overflow-y-auto">
            {!selMain && <div className="text-center py-12 text-xs text-slate-400">Select a Main Category</div>}
            {subs.map((s) => (
              <div
                key={s.id}
                className={`flex items-center justify-between px-4 py-2.5 border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${selSub === s.id ? "bg-indigo-50" : ""}`}
                onClick={() => setSelSub(s.id)}
                data-testid={`sub-cat-${s.id}`}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <ChevronRight className={`w-3 h-3 ${selSub === s.id ? "text-indigo-600" : "text-slate-300"}`} />
                  <span className="text-sm text-slate-800 truncate">{s.name}</span>
                </div>
                <div className="flex items-center gap-0.5">
                  {canEditSub && <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); startSub(s); }} className="h-7 w-7 p-0"><Pencil className="w-3 h-3" /></Button>}
                  {canDeleteSub && <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteSub(s); }} className="h-7 w-7 p-0 text-rose-600"><Trash2 className="w-3 h-3" /></Button>}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* ISSUE TYPES */}
        <Card className="border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="font-heading text-sm">Issue Types <span className="text-[10px] text-slate-400 font-normal">(carries auto-fill)</span></CardTitle>
            {canManageIssue && (
              <Button size="sm" variant="outline" onClick={() => startIssue(null)} disabled={!selSub} data-testid="add-issue-type">
                <Plus className="w-3.5 h-3.5" />
              </Button>
            )}
          </CardHeader>
          <CardContent className="p-0 max-h-[400px] overflow-y-auto">
            {!selSub && <div className="text-center py-12 text-xs text-slate-400">Select a Sub-Category</div>}
            {issues.map((i) => (
              <div key={i.id} className="px-4 py-3 border-b border-slate-100 hover:bg-slate-50" data-testid={`issue-type-${i.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800">{i.name}</div>
                    <div className="text-[11px] text-slate-500 mt-1 space-x-2">
                      <Badge variant="outline" className="text-[9px] bg-slate-50">{deptName(i.department_id)}</Badge>
                      <Badge variant="outline" className="text-[9px] capitalize bg-slate-50">{i.priority}</Badge>
                      <Badge variant="outline" className="text-[9px] bg-slate-50">SLA {fmtSla(slaResMins(i.priority))}</Badge>
                      <Badge variant="outline" className="text-[9px] bg-slate-50">{caseName(i.case_type_id)}</Badge>
                      {i.branch_required && <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200">branch req</Badge>}
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    {canManageIssue && <Button variant="ghost" size="sm" onClick={() => startIssue(i)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3" /></Button>}
                    {canManageIssue && <Button variant="ghost" size="sm" onClick={() => deleteIssue(i)} className="h-7 w-7 p-0 text-rose-600"><Trash2 className="w-3 h-3" /></Button>}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Main dialog */}
      <Dialog open={mainOpen} onOpenChange={setMainOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{editMain ? "Edit" : "Add"} Main Category</DialogTitle></DialogHeader>
          <div className="py-2 space-y-3">
            <div className="space-y-1.5"><Label>Name</Label><Input value={mainForm.name} onChange={(e) => setMainForm({ ...mainForm, name: e.target.value })} autoFocus /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMainOpen(false)}>Cancel</Button>
            <Button onClick={submitMain} className="bg-indigo-600 hover:bg-indigo-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sub dialog */}
      <Dialog open={subOpen} onOpenChange={setSubOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{editSub ? "Edit" : "Add"} Sub-Category</DialogTitle></DialogHeader>
          <div className="py-2 space-y-3">
            <div className="space-y-1.5"><Label>Name</Label><Input value={subForm.name} onChange={(e) => setSubForm({ ...subForm, name: e.target.value })} autoFocus /></div>
            <div className="space-y-1.5">
              <Label>Under Main Category</Label>
              <Select value={subForm.main_category_id} onValueChange={(v) => setSubForm({ ...subForm, main_category_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{mains.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubOpen(false)}>Cancel</Button>
            <Button onClick={submitSub} className="bg-indigo-600 hover:bg-indigo-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Issue dialog */}
      <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{editIssue ? "Edit" : "Add"} Issue Type</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">Auto-fill rules for tickets created with this Issue Type.</p>
          </DialogHeader>
          <div className="py-2 grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5"><Label>Issue Type name</Label><Input value={issueForm.name} onChange={(e) => setIssueForm({ ...issueForm, name: e.target.value })} autoFocus /></div>
            <div className="space-y-1.5">
              <Label>Department (auto)</Label>
              <Select value={issueForm.department_id} onValueChange={(v) => setIssueForm({ ...issueForm, department_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Case Type (auto)</Label>
              <Select value={issueForm.case_type_id} onValueChange={(v) => setIssueForm({ ...issueForm, case_type_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{caseTypes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Priority (auto)</Label>
              <Select value={issueForm.priority} onValueChange={(v) => setIssueForm({ ...issueForm, priority: v })}>
                <SelectTrigger data-testid="issue-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{priorityOptions.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>SLA (from policy)</Label>
              <div className="h-10 flex items-center px-3 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-600" data-testid="issue-resolved-sla">
                FR {fmtSla(slaFrMins(issueForm.priority))} · Res {fmtSla(slaResMins(issueForm.priority))}
              </div>
              <p className="text-[10px] text-slate-400">Maintained in SLA Policies — resolved live by priority.</p>
            </div>
            <div className="col-span-2 flex items-center gap-3 mt-1">
              <input
                type="checkbox"
                checked={issueForm.branch_required}
                onChange={(e) => setIssueForm({ ...issueForm, branch_required: e.target.checked })}
                className="rounded"
              />
              <Label className="cursor-pointer text-sm" onClick={() => setIssueForm({ ...issueForm, branch_required: !issueForm.branch_required })}>
                Branch / Location selection required
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIssueOpen(false)}>Cancel</Button>
            <Button onClick={submitIssue} className="bg-indigo-600 hover:bg-indigo-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkTaxonomyDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        onImported={() => { loadMains(); }}
      />
    </div>
  );
}
